import clsx from 'clsx'
import { AlertTriangle, CheckCircle2, Clock, XCircle, type LucideIcon } from 'lucide-react'
import { useServerSequence } from '@/hooks/useInfra'
import { useVmSizeCheck } from '@/hooks/useVmSizeCheck'
import { lookupErrorMessage } from '@/services/infraLive'
import { asksRdsVmCount, rdsVmCount, useInfrastructureWizard } from '@/stores/infrastructureWizard'
import { resolveServerName } from '@/utils/infraValidation'
import { hasNamingRule, sequenceKey, serverNameSeries } from '@/utils/naming'
import {
  SET_CHECK_STATUS_TEXT,
  namesRow,
  networkRow,
  setCheckTitle,
  sizeRow,
  summariseSetCheck,
  type NamesState,
  type SetCheckRow,
  type SetCheckStatus,
} from '@/utils/rdsSetCheck'
import { choiceOf, sizeWhere } from '@/utils/vmSizes'
import styles from './Infrastructure.module.css'

const ICONS: Readonly<Record<SetCheckStatus, LucideIcon>> = { ok: CheckCircle2, warning: AlertTriangle, problem: XCircle, pending: Clock }
const ROW_STYLES: Readonly<Record<SetCheckStatus, string>> = {
  ok: styles.setCheckOk,
  warning: styles.setCheckWarning,
  problem: styles.setCheckProblem,
  pending: styles.setCheckPending,
}

/**
 * "Can these 3 RDS VMs be created?" The readiness of the WHOLE set of RDS VMs a Dedicated RDS server asks for, one row per check: the names
 * (all of them), the VM size with the vCPU quota the set needs, and a subnet with a free IP for each. Every row says its state in words as
 * well as in colour and an icon, and the line under them is the overall answer. It only says what the page already knows (the same lookups
 * as the name card and the VM size card, which are shared, not asked again); what blocks Next is still `validateInfrastructure`'s, and its
 * messages stay in "To continue". The backend checks the whole set again when the deployment is dispatched.
 */
export function RdsSetCheck() {
  const product = useInfrastructureWizard((s) => s.product)
  const environment = useInfrastructureWizard((s) => s.environment)
  const region = useInfrastructureWizard((s) => s.region)
  const rdsServer = useInfrastructureWizard((s) => s.rdsServer)
  const serverSequences = useInfrastructureWizard((s) => s.serverSequences)
  const manualSqlServerName = useInfrastructureWizard((s) => s.manualSqlServerName)
  const vmSizeChoices = useInfrastructureWizard((s) => s.vmSizeChoices)
  const count = rdsVmCount({ product, rdsServer })
  const sizes = useVmSizeCheck(count)

  // The names: the same number lookup as the name card above (one query, shared), for all `count` names.
  const environmentId = environment?.id ?? ''
  const named = hasNamingRule(product)
  const series = named ? serverNameSeries({ product, environmentId, role: 'rds' }) : null
  const seriesParts = series?.ok ? series.series : null
  const sequence = useServerSequence(environmentId, product, 'rds', seriesParts !== null)
  const key = seriesParts ? sequenceKey(environmentId, seriesParts) : ''
  const sequences = key && sequence.data ? { [key]: sequence.data, ...serverSequences } : serverSequences

  if (!product || !asksRdsVmCount({ product, rdsServer })) return null

  const namesState: NamesState =
    seriesParts && sequence.isLoading
      ? { state: 'waiting' }
      : seriesParts && sequence.isError
        ? { state: 'failed', message: lookupErrorMessage(sequence.error) ?? 'Could not work out the next free number for these names.' }
        : { state: 'resolved', resolved: resolveServerName({ product, environment, serverSequences: sequences, manualSqlServerName }, 'rds', 'host', count) }
  const rows: SetCheckRow[] = [
    ...(named ? [namesRow(namesState)] : []),
    sizeRow({
      check: sizes.rds,
      count,
      loading: sizes.isLoading,
      quotaChecked: sizes.quotaChecked,
      note: sizes.note,
      choice: sizes.rds ? choiceOf(sizes.rds, vmSizeChoices, sizeWhere({ environment, region })) : null,
    }),
    networkRow(rdsServer.network, count),
  ]
  const summary = summariseSetCheck(rows, count)
  const title = setCheckTitle(count)

  return (
    <section className={styles.setCheck} aria-label={title} data-testid="rds-set-check">
      <h5 className={styles.setCheckTitle}>{title}</h5>
      <ul className={styles.setCheckList}>
        {rows.map((row) => {
          const Icon = ICONS[row.status]
          return (
            <li key={row.id} className={clsx(styles.setCheckRow, ROW_STYLES[row.status])} data-testid={`rds-set-check-${row.id}`} data-status={row.status}>
              <Icon size={15} aria-hidden="true" className={styles.setCheckIcon} />
              <span className={styles.setCheckStatus}>{SET_CHECK_STATUS_TEXT[row.status]}</span>
              <div className={styles.setCheckBody}>
                <strong>{row.label}</strong>
                <span className={styles.setCheckText}>{row.text}</span>
              </div>
            </li>
          )
        })}
      </ul>
      <p
        role={summary.kind === 'issues' ? 'alert' : 'status'}
        className={clsx(styles.setCheckSummary, summary.kind === 'issues' && styles.setCheckSummaryIssues)}
        data-testid="rds-set-check-summary"
        data-summary={summary.kind}
      >
        {summary.text}
      </p>
    </section>
  )
}
